/**
 * Follow-up scheduler action handlers extracted verbatim from
 * `ContactEditModal.tsx` and parameterized on a typed deps object.
 *
 * The orchestrator keeps the (non-memoized) handler shells and delegates here,
 * so behavior is unchanged (#3293 Pass-2). Covers: save (nextStep only, no
 * note), complete (completion note + optional re-schedule + state reset),
 * snooze (+7d), remove (clear nextStep), and quick (relative-offset set +
 * optional persist for existing contacts).
 */

import type { Activity, ContactObject } from './types';
import { activitiesToNotes } from './contactMapping';

interface FollowUpAuthorProfile {
  id?: string;
  name?: string;
}

type FollowUpTracker = { action: string; dueAt: string } | null;

export interface FollowUpDeps {
  nextStepDueAt: string;
  nextStepAction: string;
  isNew: boolean | undefined;
  contact: ContactObject | null | undefined;
  activities: Activity[];
  setActivities: (a: Activity[]) => void;
  profile: FollowUpAuthorProfile | null | undefined;
  setNextStepAction: (s: string) => void;
  setNextStepDueAt: (s: string) => void;
  setLastSavedFollowUp: (t: FollowUpTracker) => void;
  nextFollowUpSchedule: 'none' | '1week' | '2weeks' | '1month' | 'custom';
  nextFollowUpDate: string;
  nextFollowUpTitle: string;
  setNextFollowUpSchedule: (s: 'none' | '1week' | '2weeks' | '1month' | 'custom') => void;
  setNextFollowUpDate: (s: string) => void;
  setNextFollowUpTitle: (s: string) => void;
  onSave: (data: Partial<ContactObject>) => void;
}

export function handleSaveFollowUp(deps: FollowUpDeps): void {
  const { nextStepDueAt, isNew, contact, nextStepAction, setLastSavedFollowUp, onSave } = deps;
  if (!nextStepDueAt || isNew || !contact) return;

  const title = nextStepAction.trim() || 'Follow Up';

  // Update the tracker
  setLastSavedFollowUp({
    action: title,
    dueAt: nextStepDueAt,
  });

  // Save only the nextStep without creating a note
  onSave({
    nextStep: {
      action: title,
      dueAt: new Date(nextStepDueAt).toISOString(),
    },
  });
}

export function handleCompleteFollowUp(deps: FollowUpDeps): void {
  const {
    nextStepDueAt, isNew, contact, nextStepAction, profile, activities, setActivities,
    nextFollowUpSchedule, nextFollowUpDate, nextFollowUpTitle, setNextStepAction,
    setNextStepDueAt, setLastSavedFollowUp, onSave, setNextFollowUpSchedule,
    setNextFollowUpDate, setNextFollowUpTitle,
  } = deps;
  if (!nextStepDueAt || isNew || !contact) return;

  // Create completion note with proper formatting
  const completedDate = new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  let noteContent: string;

  if (!nextStepAction.trim()) {
    // No title - just "Follow Up"
    noteContent = 'Follow Up';
  } else {
    // Has title - "Follow Up: title"
    noteContent = `Follow Up: ${nextStepAction.trim()}`;
  }

  // Always add Completed On
  noteContent += `\nCompleted On: ${completedDate}`;

  const newActivity: Activity = {
    id: Date.now().toString(),
    date: new Date().toISOString(),
    note: noteContent,
    authorId: profile?.id || '',
    authorName: profile?.name || 'Unknown',
  };

  const updatedActivities = [newActivity, ...activities];
  setActivities(updatedActivities);

  // Set new next step based on selected schedule
  let newNextStep;
  if (nextFollowUpSchedule === 'none' || !nextFollowUpDate) {
    // No follow-up scheduled
    newNextStep = null;
    setNextStepAction('');
    setNextStepDueAt('');
    setLastSavedFollowUp(null);
  } else {
    newNextStep = {
      action: nextFollowUpTitle.trim() || 'Follow Up',
      dueAt: nextFollowUpDate,
    };
    setNextStepAction(newNextStep.action);
    setNextStepDueAt(newNextStep.dueAt);
    setLastSavedFollowUp(newNextStep);
  }

  // Save with note added and new nextStep
  onSave({
    nextStep: newNextStep,
    activities: updatedActivities,
    notes: activitiesToNotes(updatedActivities),
  } as any);

  // Reset modal state
  setNextFollowUpSchedule('none');
  setNextFollowUpDate('');
  setNextFollowUpTitle('');
}

export function handleSnoozeFollowUp(deps: FollowUpDeps): void {
  const { nextStepDueAt, isNew, contact, nextStepAction, setNextStepDueAt, setLastSavedFollowUp, onSave } = deps;
  if (!nextStepDueAt || isNew || !contact) return;

  const newDate = new Date();
  newDate.setDate(newDate.getDate() + 7);
  const newDueAt = newDate.toISOString().split('T')[0];

  const title = nextStepAction.trim() || 'Follow Up';

  setNextStepDueAt(newDueAt);
  setLastSavedFollowUp({ action: title, dueAt: newDueAt });

  onSave({
    nextStep: {
      action: title,
      dueAt: newDate.toISOString(),
    },
  });
}

export function handleRemoveFollowUp(deps: FollowUpDeps): void {
  const { isNew, contact, setNextStepAction, setNextStepDueAt, setLastSavedFollowUp, onSave } = deps;
  if (isNew || !contact) return;

  setNextStepAction('');
  setNextStepDueAt('');
  setLastSavedFollowUp(null);

  onSave({
    nextStep: null,
  } as any);
}

export function handleQuickFollowUp(offset: { days?: number; months?: number }, deps: FollowUpDeps): void {
  const { nextStepAction, isNew, contact, setNextStepDueAt, setLastSavedFollowUp, onSave } = deps;
  const newDate = new Date();
  if (offset.days) {
    newDate.setDate(newDate.getDate() + offset.days);
  }
  if (offset.months) {
    newDate.setMonth(newDate.getMonth() + offset.months);
  }
  const newDueAt = newDate.toISOString().split('T')[0];
  const title = nextStepAction.trim() || 'Follow Up';

  // Don't populate the input field, just set the date
  setNextStepDueAt(newDueAt);

  if (!isNew && contact) {
    setLastSavedFollowUp({ action: title, dueAt: newDueAt });

    onSave({
      nextStep: {
        action: title,
        dueAt: newDate.toISOString(),
      },
    });
  } else {
    setLastSavedFollowUp(null);
  }
}
